import { useLingui } from "@lingui/react/macro";
import type { MessageReaction, ThreadMessage } from "@rakazo/contracts";
import { canReactToThreadMessage, MESSAGE_REACTIONS } from "@rakazo/contracts";
import { Popover, PopoverContent, PopoverTrigger, toast } from "@rakazo/ui-web";
import { Check, Copy, Reply, Share2, Smile } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MessageHoverMetadata } from "../../components/MessageHoverMetadata";
import { copyableMessageText } from "../../lib/message-text";
import { MessageTime } from "./chat-context";

export function MessageHoverActions({
  message,
  side,
  onReply,
  onReact,
  onShare,
}: {
  message: ThreadMessage;
  side: "start" | "end";
  onReply: (message: ThreadMessage) => void;
  onReact: (message: ThreadMessage, reaction: MessageReaction) => Promise<void>;
  onShare?: (message: ThreadMessage) => void;
}) {
  const { t, i18n } = useLingui();
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const copyAttempt = useRef(0);
  useEffect(
    () => () => {
      copyAttempt.current += 1;
      clearTimeout(copiedTimer.current);
    },
    [],
  );

  // Streaming progress bubbles keep hover free for selection / stop clicks.
  if (message.id.startsWith("progress:")) return null;

  async function copyMessage() {
    const text = copyableMessageText(message);
    if (!text) return;
    const attempt = ++copyAttempt.current;
    clearTimeout(copiedTimer.current);
    setCopied(false);
    try {
      await navigator.clipboard.writeText(text);
      if (attempt !== copyAttempt.current) return;
      setCopied(true);
      copiedTimer.current = setTimeout(() => setCopied(false), 2_000);
    } catch {
      if (attempt === copyAttempt.current) toast.error(t`Could not copy. Try again.`);
    }
  }

  const iconButtonClass =
    "message-action-button grid shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring";

  return (
    <MessageHoverMetadata
      pinned={reactionsOpen || copied}
      side={side}
      timestamp={<MessageTime createdAt={message.createdAt} locale={i18n.locale || "en"} />}
    >
      <div data-testid="message-hover-actions" className="message-action-buttons">
        {canReactToThreadMessage(message) ? (
          <Popover open={reactionsOpen} onOpenChange={setReactionsOpen}>
            <PopoverTrigger aria-label={t`React`} className={iconButtonClass}>
              <Smile size={15} strokeWidth={1.7} />
            </PopoverTrigger>
            <PopoverContent
              align={side === "end" ? "start" : "end"}
              className="w-auto flex-row gap-0 rounded-2xl p-1.5"
              aria-label={t`Reactions`}
            >
              {MESSAGE_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  aria-label={emoji}
                  className="grid h-11 w-11 place-items-center rounded-xl text-2xl hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
                  onClick={() => {
                    setReactionsOpen(false);
                    void onReact(message, emoji);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        ) : null}
        <button
          type="button"
          aria-label={t`Reply`}
          onClick={() => onReply(message)}
          className={iconButtonClass}
        >
          <Reply size={15} strokeWidth={1.7} />
        </button>
        <button
          type="button"
          aria-label={t`Copy`}
          title={copied ? t`Copied` : t`Copy`}
          className={iconButtonClass}
          onClick={copyMessage}
        >
          {copied ? (
            <Check size={15} strokeWidth={2} className="text-success" />
          ) : (
            <Copy size={15} strokeWidth={1.7} />
          )}
          <span role="status" className="sr-only">
            {copied ? t`Copied` : ""}
          </span>
        </button>
        {onShare ? (
          <button
            type="button"
            aria-label={t`Share to a room`}
            className={iconButtonClass}
            onClick={() => onShare(message)}
          >
            <Share2 size={15} strokeWidth={1.7} />
          </button>
        ) : null}
      </div>
    </MessageHoverMetadata>
  );
}
